import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Box,
    Button,
    FormControlLabel,
    IconButton,
    Switch,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { validateCronExpression } from "../../components/CronExpressionBuilder";
import CustomSnackbar from "../../components/CustomSnackbar";
import { getAllPuzzleTypes } from "../../pzl_common/PuzzleTypeConstants";
import PublisherScheduleService from "./PublisherScheduleService";
import PubTabLogs from "./PubTabLogs";
import PubTabScheduler from "./PubTabScheduler";

const LANGUAGES = [
    { value: "all", label: "All" },
    { value: "english", label: "English" },
    { value: "french", label: "French" }
];

const DEFAULT_CRON = "0 0 9,13 ? * MON,WED";

function normalizeScheduleLanguage(raw) {
    const lang = (raw || "").toLowerCase();
    if (lang === "fr" || lang === "french") return "french";
    if (lang === "all") return "all";
    return "english";
}

export default function PublisherScheduleFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(isEdit);
    const [cron, setCron] = useState(DEFAULT_CRON);
    const [language, setLanguage] = useState("english");
    const [puzzleType, setPuzzleType] = useState("WORD_SEARCH");
    const [enabled, setEnabled] = useState(false);
    const [tagBasedCrossword, setTagBasedCrossword] = useState(false);
    const [memo, setMemo] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");

    const [tabValue, setTabValue] = useState(0);

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        const fetchSchedule = async () => {
            setLoading(true);
            const result = await PublisherScheduleService.getSchedule(id);
            setLoading(false);
            if (cancelled) return;
            if (result.success && result.data) {
                const s = result.data;
                setCron(s.cron || "");
                setPuzzleType(s.puzzleType || "WORD_SEARCH");
                setLanguage(normalizeScheduleLanguage(s.language));
                setEnabled(s.enabled ?? false);
                setTagBasedCrossword(Boolean(s.tagBasedCrossword));
                setMemo(s.memo ?? "");
            } else {
                setMessage(result.error || "Failed to load schedule");
                setSeverity("error");
                setOpenSnackbar(true);
            }
        };
        fetchSchedule();
        return () => { cancelled = true; };
    }, [id, isEdit]);

    const handleSnackBar = (sev, msg) => {
        setOpenSnackbar(true);
        setSeverity(sev);
        setMessage(msg);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validation = validateCronExpression(cron);
        if (!validation.valid) {
            handleSnackBar("error", validation.error);
            return;
        }
        const basePayload = {
            cron: cron.trim(),
            language,
            puzzleType,
            enabled,
            tagBasedCrossword: puzzleType === "CROSSWORD" ? tagBasedCrossword : false
        };
        const trimmedMemo = memo.trim();
        const formData = isEdit
            ? { ...basePayload, memo: trimmedMemo === "" ? null : trimmedMemo }
            : trimmedMemo === ""
                ? basePayload
                : { ...basePayload, memo: trimmedMemo };
        (async () => {
            const result = isEdit
                ? await PublisherScheduleService.updateSchedule(id, formData)
                : await PublisherScheduleService.createSchedule(formData);
            if (result.success) {
                navigate("/admin/publisher", { state: { message: isEdit ? "Schedule updated successfully" : "Schedule created successfully" } });
            } else {
                handleSnackBar("error", result.error);
            }
        })();
    };

    const handleCancel = () => {
        navigate("/admin/publisher");
    };

    const handlePuzzleTypeChange = (nextType) => {
        setPuzzleType(nextType);
        if (nextType !== "CROSSWORD") {
            setTagBasedCrossword(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ flexGrow: 1, p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <IconButton aria-label="Back to list" onClick={() => navigate("/admin/publisher")} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography>Loading...</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                <IconButton
                    aria-label="Back to list"
                    onClick={() => navigate("/admin/publisher")}
                    size="small"
                    sx={{ mr: 0.5 }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                    {isEdit ? "Edit Publisher Schedule" : "New Publisher Schedule"}
                </Typography>
            </Box>

            {isEdit ? (
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Tab label="Scheduler" />
                    <Tab label="Logs" />
                </Tabs>
            ) : null}

            {(!isEdit || tabValue === 0) && (
                <PubTabScheduler
                    cron={cron}
                    onCronChange={setCron}
                    language={language}
                    onLanguageChange={setLanguage}
                    puzzleType={puzzleType}
                    onPuzzleTypeChange={handlePuzzleTypeChange}
                    enabled={enabled}
                    onEnabledChange={setEnabled}
                    tagBasedCrossword={tagBasedCrossword}
                    onTagBasedCrosswordChange={setTagBasedCrossword}
                    memo={memo}
                    onMemoChange={setMemo}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    isEdit={isEdit}
                />
            )}

            {isEdit && tabValue === 1 && (
                <Box sx={{ mt: 2 }}>
                    <PubTabLogs
                        schedules={id ? [{ id, language, cron }] : []}
                        defaultScheduleId={id || ""}
                        onError={handleSnackBar}
                    />
                </Box>
            )}

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
        </Box>
    );
}
